import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Calendar, 
  CheckCircle2, 
  ArrowLeft,
  Building2,
  User,
  Mail,
  Phone,
  MessageSquare,
  Loader2,
  Zap,
  Shield,
  BarChart3,
  GitBranch,
  Users,
  Target,
  AlertCircle
} from 'lucide-react';

// Import logo
import { LOGO_COLLAPSED_DATA } from '../../assets/logoInline';

const API_URL = window.location.origin;

export default function DemoPage() {
  const [searchParams] = useSearchParams();
  const [formState, setFormState] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    agencySize: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormState('loading');
    setErrorMessage('');
    
    try {
      const response = await fetch(`${API_URL}/api/marketing/demo-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          agency_size: formData.agencySize,
          message: formData.message,
          utm_source: searchParams.get('utm_source') || '',
          utm_medium: searchParams.get('utm_medium') || '',
          utm_campaign: searchParams.get('utm_campaign') || '',
          referrer: document.referrer || '',
        }),
      });
      
      if (response.ok) {
        setFormState('success');
      } else {
        const data = await response.json();
        setErrorMessage(data.detail || 'Failed to submit demo request. Please try again.');
        setFormState('error');
      }
    } catch (error) {
      console.error('Demo request error:', error);
      setErrorMessage('Network error. Please check your connection and try again.');
      setFormState('error');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Success State
  if (formState === 'success') {
    return (
      <main className="min-h-screen pt-32 pb-24 flex items-center" data-testid="demo-success-page">
        <div className="max-w-xl mx-auto px-6 md:px-8 text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 className="w-10 h-10 text-emerald-400" strokeWidth={1.5} />
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-4">
            Demo Request Received
          </h1>
          <p className="text-lg text-slate-300/80 mb-8">
            Thank you for your interest in MissionAssist. Our team will reach out within 24 hours to schedule your personalized demo.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-indigo-400 font-medium hover:text-indigo-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </main>
    );
  }

  // What You'll See in the Demo
  const demoHighlights = [
    {
      icon: Target,
      text: 'How MissionAssist captures and structures lead intake from any funnel',
    },
    {
      icon: Calendar,
      text: 'How booking flows convert lead submissions into scheduled appointments',
    },
    {
      icon: Shield,
      text: 'How compliance records (TCPA, recordings, consent) are stored automatically',
    },
    {
      icon: GitBranch,
      text: 'How workflows, triggers, and automation work together',
    },
    {
      icon: BarChart3,
      text: 'How operational visibility helps you see what\'s actually producing business',
    },
  ];

  return (
    <main className="min-h-screen pt-32 pb-24" data-testid="demo-page">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left - Content */}
          <div className="lg:pt-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-slate-400 text-sm font-medium hover:text-white transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>

            {/* Logo */}
            <div className="flex items-center gap-3 mb-8">
              <img src={LOGO_COLLAPSED_DATA} alt="MissionAssist ORBIT" className="w-10 h-10 rounded-lg" />
              <span className="text-lg font-semibold text-white">MissionAssist</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-white leading-[1.1] mb-6">
              See MissionAssist in Action
            </h1>
            <p className="text-lg text-slate-300/80 mb-10 leading-relaxed">
              Book a personalized demo to see how MissionAssist connects your leads, bookings, compliance, and workflows.
            </p>

            {/* What You'll See Section */}
            <div className="glass rounded-xl p-6 mb-8">
              <h2 className="text-lg font-semibold text-white mb-4">
                What You'll See in the Demo
              </h2>
              <ul className="space-y-4">
                {demoHighlights.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-indigo-400" strokeWidth={1.5} />
                    </div>
                    <span className="text-slate-300/90 text-sm leading-relaxed">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Target Audience Note */}
            <div className="glass rounded-xl p-4 border border-indigo-500/20 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm text-white font-medium mb-1">Best for:</p>
                  <p className="text-sm text-slate-400">
                    Agency owners and operators running mortgage protection, final expense, Medicare, or life insurance operations who want more control over lead flow and agency operations.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Demo Details */}
            <div className="space-y-6">
              {[
                {
                  icon: Calendar,
                  title: '30-Minute Demo',
                  description: 'See the platform in action with a personalized walkthrough.',
                },
                {
                  icon: Building2,
                  title: 'Tailored to Your Agency',
                  description: "We'll focus on the workflows most relevant to your business.",
                },
                {
                  icon: MessageSquare,
                  title: 'Q&A Included',
                  description: 'Get answers to your questions about implementation and features.',
                },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-indigo-400" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                    <p className="text-slate-400 text-sm">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Right - Form */}
          <div>
            <div className="glass rounded-2xl p-8 md:p-10">
              {/* Logo above form */}
              <div className="flex items-center gap-3 mb-6 pb-6 border-b border-white/5">
                <img src={LOGO_COLLAPSED_DATA} alt="MissionAssist" className="w-8 h-8 rounded-lg" />
                <span className="text-sm font-medium text-slate-300">Request a Demo</span>
              </div>
              
              <h2 className="text-2xl font-semibold text-white mb-6">
                Book Your Demo
              </h2>

              {/* Error Message */}
              {formState === 'error' && (
                <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-300">{errorMessage}</p>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="demo-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-slate-300 mb-2">
                      First Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" strokeWidth={1.5} />
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        className="w-full h-11 pl-10 pr-4 rounded-lg bg-white/5 text-white placeholder-slate-500 border border-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-transparent transition-all"
                        placeholder="John"
                        data-testid="demo-first-name"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-slate-300 mb-2">
                      Last Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" strokeWidth={1.5} />
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        className="w-full h-11 pl-10 pr-4 rounded-lg bg-white/5 text-white placeholder-slate-500 border border-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-transparent transition-all"
                        placeholder="Smith"
                        data-testid="demo-last-name"
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                    Work Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" strokeWidth={1.5} />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full h-11 pl-10 pr-4 rounded-lg bg-white/5 text-white placeholder-slate-500 border border-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-transparent transition-all"
                      placeholder="john@agency.com"
                      data-testid="demo-email"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-300 mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" strokeWidth={1.5} />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full h-11 pl-10 pr-4 rounded-lg bg-white/5 text-white placeholder-slate-500 border border-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-transparent transition-all"
                      placeholder="(555) 123-4567"
                      data-testid="demo-phone"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-slate-300 mb-2">
                    Agency Name
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" strokeWidth={1.5} />
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full h-11 pl-10 pr-4 rounded-lg bg-white/5 text-white placeholder-slate-500 border border-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-transparent transition-all"
                      placeholder="Smith Insurance Agency"
                      data-testid="demo-company"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="agencySize" className="block text-sm font-medium text-slate-300 mb-2">
                    Agency Size
                  </label>
                  <select
                    id="agencySize"
                    name="agencySize"
                    value={formData.agencySize}
                    onChange={handleChange}
                    className="w-full h-11 px-4 rounded-lg bg-white/5 text-white border border-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-transparent transition-all"
                    data-testid="demo-agency-size"
                  >
                    <option value="" className="bg-slate-900">Select agency size...</option>
                    <option value="1-5" className="bg-slate-900">1-5 agents</option>
                    <option value="6-15" className="bg-slate-900">6-15 agents</option>
                    <option value="16-50" className="bg-slate-900">16-50 agents</option>
                    <option value="50+" className="bg-slate-900">50+ agents</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">
                    What would you like to see? (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 text-white placeholder-slate-500 border border-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-transparent transition-all resize-none"
                    placeholder="Tell us about your agency and what workflows you're interested in..."
                    data-testid="demo-message"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={formState === 'loading'}
                  className="w-full h-12 rounded-full bg-indigo-500 text-white font-medium hover:bg-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-500/25"
                  data-testid="demo-submit-button"
                >
                  {formState === 'loading' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Request Demo'
                  )}
                </button>
                
                <p className="text-xs text-slate-500 text-center">
                  By submitting this form, you agree to receive communications from MissionAssist.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
